import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
    Table,
    TableHeader,
    TableBody,
    TableHead,
    TableRow,
    TableCell,
} from "@/components/ui/table";
import { eur, num } from "@/lib/format";
import { Sparkles, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";

// Definition der Auto-Tags (Reihenfolge der Tab-Buttons)
const TAG_DEFS = [
    { key: "ne", label: "NE", title: "Neuerscheinungen", description: "ET = Periode mit Auflage 1, oder ET = Folgejahr (NE FJ)" },
    { key: "nd", label: "ND", title: "Nachdrucke", description: "AA = Periode UND (ET < AA ODER Auflage ≥ 2 im selben Jahr)" },
    { key: "r", label: "R", title: "Ramsch", description: "Sonder-WB-Liste Typ Verramschung — pro Periode aktiv" },
    { key: "m", label: "M", title: "Makulatur", description: "Sonder-WB-Liste Typ Makulatur — pro Periode aktiv" },
];

const matchesTag = (movement, tagKey) => {
    if (tagKey === "ne") return !!movement.tag_ne;
    if (tagKey === "nd") return !!movement.tag_nd;
    if (tagKey === "r") return !!movement.tag_r;
    if (tagKey === "m") return !!movement.tag_m;
    return false;
};

export default function Reports() {
    const { activeTenant } = useTenant();
    const { zeitraum, setZeitraum } = usePeriod();
    const [movements, setMovements] = useState([]);
    const [activeTag, setActiveTag] = useState("ne");
    const [programmTag, setProgrammTag] = useState("ne");
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", expiresInSec: 600 });

    useEffect(() => {
        if (!activeTenant) return;
        let abort = false;
        api.get("/movements", {
            params: { tenant_id: activeTenant.tenant_id, zeitraum },
        })
            .then((m) => { if (!abort) setMovements(m.data); })
            .catch(() => { if (!abort) toast.error("Berichte konnten nicht geladen werden"); });
        return () => { abort = true; };
    }, [activeTenant, zeitraum]);

    // Programm-Übersicht: Aggregate für den gewählten Tag (NE oder ND).
    // Die Struktur ist identisch — nur die Daten in „totalSelected/lagerSelected"
    // und das „Davon"-Sub-KPI ändern sich:
    //   * NE: „Davon Vorab" (ET = Periode+1, potenziell zu früh produziert)
    //   * ND: „Davon Auflage 3+" (ET=AA=Periode UND Auflage ≥ 3, „Hit-Indikator")
    const programmStats = useMemo(() => {
        const periodYear = parseInt(zeitraum, 10);
        const vorabYear = periodYear + 1;
        const isNE = programmTag === "ne";
        const total = movements.length;
        const matches = (m) => (isNE ? !!m.tag_ne : !!m.tag_nd);
        // Sub-KPI „Davon …":
        const subMatches = (m) => {
            if (isNE) return m.et_jahr === vorabYear;
            // ND: ET=AA=Periode UND Auflage ≥ 3
            return (
                !!m.tag_nd
                && m.et_jahr === periodYear
                && (m.aa_jahr === periodYear || !m.aa_jahr)
                && (m.auflage_nr || 0) >= 3
            );
        };
        const totalSel = movements.filter(matches).length;
        const totalSub = movements.filter(subMatches).length;
        const lagerTotal = movements.reduce((s, m) => s + (m.lagerwert || 0), 0);
        const lagerSel = movements.filter(matches).reduce((s, m) => s + (m.lagerwert || 0), 0);
        const lagerSub = movements.filter(subMatches).reduce((s, m) => s + (m.lagerwert || 0), 0);
        const byEg = {};
        for (const m of movements) {
            const eg = (m.erloesgruppe || "Ohne Erlösgruppe").trim() || "Ohne Erlösgruppe";
            if (!byEg[eg]) byEg[eg] = {
                erloesgruppe: eg, total: 0, sel: 0, sub: 0,
                lager_total: 0, lager_sel: 0, lager_sub: 0,
            };
            byEg[eg].total += 1;
            byEg[eg].lager_total += m.lagerwert || 0;
            if (matches(m)) {
                byEg[eg].sel += 1;
                byEg[eg].lager_sel += m.lagerwert || 0;
            }
            if (subMatches(m)) {
                byEg[eg].sub += 1;
                byEg[eg].lager_sub += m.lagerwert || 0;
            }
        }
        const abbrev = (s) => {
            const c = String(s || "").replace(/^\d+\s*:\s*/, "").trim();
            if (/kinder.*jugend/i.test(c)) return "KJ";
            if (/annette\s*betz/i.test(c)) return "AB";
            const w = c.split(/\s+/).filter(Boolean);
            return w.length >= 2 ? (w[0][0] + w[1][0]).toUpperCase() : c.slice(0, 2).toUpperCase();
        };
        const groups = Object.values(byEg)
            .map((g) => ({
                ...g,
                kuerzel: abbrev(g.erloesgruppe),
                quote: g.total ? (g.sel / g.total) * 100 : 0,
                lager_quote: g.lager_total ? (g.lager_sel / g.lager_total) * 100 : 0,
            }))
            .sort((a, b) => a.erloesgruppe.localeCompare(b.erloesgruppe));
        return {
            periodYear,
            vorabYear,
            isNE,
            total,
            totalSel,
            totalSub,
            quote: total ? (totalSel / total) * 100 : 0,
            lager_total: lagerTotal,
            lager_sel: lagerSel,
            lager_sub: lagerSub,
            lager_quote: lagerTotal ? (lagerSel / lagerTotal) * 100 : 0,
            groups,
        };
    }, [movements, zeitraum, programmTag]);

    // Theme pro Tag — alle Tailwind-Klassen vorab als String, damit JIT sie erfasst.
    const THEMES = {
        ne: {
            sparkle: "text-amber-300",
            iconLabel: "Neuerscheinungen",
            subLabel: (p) => `Davon Vorab (ET ${p.vorabYear})`,
            subSuffix: "NE",
            primaryCountLabel: (p) => `Anzahl NE (ET ${p.periodYear}+${p.vorabYear})`,
            lagerLabel: "Lagerwert NE gesamt",
            box: "border-amber-500/30 bg-amber-500/5 divide-amber-500/20",
            primary: "text-amber-300",
            sub: "text-orange-300",
            egBox: "border-amber-500/30 bg-amber-500/5",
            egCircle: "bg-amber-500/20 text-amber-300 border-amber-500/40",
            egDivider: "border-amber-500/20",
            barBg: "bg-amber-500/10",
            barFill: "bg-amber-400",
            barPercent: "text-amber-300",
            barTopBorder: "border-amber-500/15",
            cardLabel: "NE",
            egCountLabel: "Anzahl NE",
            egSubLabel: "Vorab",
            egLagerLabel: "Lagerwert NE",
        },
        nd: {
            sparkle: "text-teal-300",
            iconLabel: "Nachdrucke",
            subLabel: () => "Davon Auflage 3+ (Hit)",
            subSuffix: "ND",
            primaryCountLabel: (p) => `Anzahl ND (AA = ${p.periodYear})`,
            lagerLabel: "Lagerwert ND gesamt",
            box: "border-teal-500/30 bg-teal-500/5 divide-teal-500/20",
            primary: "text-teal-300",
            sub: "text-cyan-300",
            egBox: "border-teal-500/30 bg-teal-500/5",
            egCircle: "bg-teal-500/20 text-teal-300 border-teal-500/40",
            egDivider: "border-teal-500/20",
            barBg: "bg-teal-500/10",
            barFill: "bg-teal-400",
            barPercent: "text-teal-300",
            barTopBorder: "border-teal-500/15",
            cardLabel: "ND",
            egCountLabel: "Anzahl ND",
            egSubLabel: "Auflage 3+",
            egLagerLabel: "Lagerwert ND",
        },
    };
    const theme = THEMES[programmTag];

    // WB- und ET-Statistiken wurden entfernt; Tag-Liste übernimmt diese Rolle.

    // Liste der Titel mit dem aktiven Tag, sortiert nach Erlösgruppe
    const tagItems = useMemo(() => {
        return [...movements]
            .filter((m) => matchesTag(m, activeTag))
            .sort((a, b) => {
                const ea = (a.erloesgruppe || "").toLowerCase();
                const eb = (b.erloesgruppe || "").toLowerCase();
                if (ea !== eb) return ea.localeCompare(eb);
                // Sekundär: Lagerwert absteigend innerhalb einer Erlösgruppe
                return (b.lagerwert || 0) - (a.lagerwert || 0);
            });
    }, [movements, activeTag]);

    const tagCounts = useMemo(() => {
        return TAG_DEFS.reduce((acc, t) => {
            acc[t.key] = movements.filter((m) => matchesTag(m, t.key)).length;
            return acc;
        }, {});
    }, [movements]);

    const tagLagerwerte = useMemo(() => {
        return TAG_DEFS.reduce((acc, t) => {
            acc[t.key] = movements
                .filter((m) => matchesTag(m, t.key))
                .reduce((s, m) => s + (m.lagerwert || 0), 0);
            return acc;
        }, {});
    }, [movements]);

    const activeTagDef = TAG_DEFS.find((t) => t.key === activeTag);

    // Anzahl Titel mit mehreren Tags (aktuell NE + ND)
    const conflictCount = useMemo(
        () => movements.filter((m) => m.tag_ne && m.tag_nd).length,
        [movements],
    );

    // Datenlücken: leeres AA-Jahr, aber Auflage ≥ 2 → potenzielle versteckte NDs
    const aaLueckenCount = useMemo(
        () => movements.filter((m) => !m.aa_jahr && (m.auflage_nr || 0) >= 2).length,
        [movements],
    );

    const exportTagConflicts = async () => {
        if (!activeTenant) return;
        try {
            const res = await api.get("/export/tag-conflicts.xlsx", {
                params: { tenant_id: activeTenant.tenant_id, zeitraum },
                responseType: "blob",
            });
            const objUrl = URL.createObjectURL(res.data);
            const filename = `tag_konflikte_${activeTenant.tenant_id}_${zeitraum}.xlsx`;
            setDlDialog({ open: true, url: objUrl, filename, expiresInSec: 600 });
        } catch (e) {
            toast.error(`Konflikt-Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
        }
    };

    const exportAaLuecken = async () => {
        if (!activeTenant) return;
        try {
            const res = await api.get("/export/datenluecken-aa.xlsx", {
                params: { tenant_id: activeTenant.tenant_id, zeitraum },
                responseType: "blob",
            });
            const objUrl = URL.createObjectURL(res.data);
            const filename = `datenluecken_aa_${activeTenant.tenant_id}_${zeitraum}.xlsx`;
            setDlDialog({ open: true, url: objUrl, filename, expiresInSec: 600 });
        } catch (e) {
            toast.error(`Datenlücken-Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
        }
    };

    const exportTagExcel = async () => {
        if (!activeTenant) return;
        try {
            const t = await api.post("/admin/download-token", {
                purpose: "tag-xlsx",
                tenant_id: activeTenant.tenant_id,
                tag: activeTag,
                zeitraum,
            });
            const token = t.data?.token;
            const ttl = t.data?.expires_in || 600;
            if (!token) throw new Error("Kein Download-Token erhalten");
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/tag-token.xlsx`
                + `?token=${encodeURIComponent(token)}`
                + `&tenant_id=${encodeURIComponent(activeTenant.tenant_id)}`
                + `&zeitraum=${encodeURIComponent(zeitraum)}`
                + `&tag=${encodeURIComponent(activeTag)}`;
            const filename = `tag_${activeTag}_${activeTenant.tenant_id}_${zeitraum}.xlsx`;
            setDlDialog({ open: true, url, filename, expiresInSec: ttl });
        } catch (e) {
            toast.error(`Excel-Export fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
        }
    };

    return (
        <div className="space-y-8 pb-12 animate-fade-up">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Berichte</div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                        Auswertungen & Statistiken
                    </h1>
                </div>
                <select
                    value={zeitraum}
                    onChange={(e) => setZeitraum(e.target.value)}
                    className="h-10 px-3 rounded-md border border-border bg-surface1 text-sm"
                    data-testid="reports-zeitraum"
                >
                    {["2023", "2024", "2025", "2026"].map((y) => (
                        <option key={y} value={y}>
                            Jahr {y}
                        </option>
                    ))}
                </select>
            </div>

            {/* Programm-Übersicht (NE oder ND, umschaltbar) */}
            <Card className="p-6 bg-surface1 border-border" data-testid="programm-summary-card">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            <Sparkles className={`h-3.5 w-3.5 ${theme.sparkle}`} />
                            {theme.iconLabel} {zeitraum}
                        </div>
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <h3 className="font-heading text-xl font-semibold">
                                Programm-Übersicht
                            </h3>
                            {/* Toggle NE ↔ ND */}
                            <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="programm-tag-toggle">
                                <button
                                    type="button"
                                    onClick={() => setProgrammTag("ne")}
                                    data-testid="programm-tag-ne"
                                    className={`px-3 py-1 text-xs font-bold tracking-wider transition ${
                                        programmTag === "ne"
                                            ? "bg-amber-500/20 text-amber-300"
                                            : "bg-transparent text-muted-foreground hover:bg-surface2"
                                    }`}
                                >
                                    NE
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setProgrammTag("nd")}
                                    data-testid="programm-tag-nd"
                                    className={`px-3 py-1 text-xs font-bold tracking-wider transition border-l border-border ${
                                        programmTag === "nd"
                                            ? "bg-teal-500/20 text-teal-300"
                                            : "bg-transparent text-muted-foreground hover:bg-surface2"
                                    }`}
                                >
                                    ND
                                </button>
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 max-w-md">
                            {programmTag === "ne" ? (
                                <>
                                    Titel mit Erstauslieferung im Bewertungsjahr {programmStats.periodYear} oder dem Folgejahr {programmStats.vorabYear}.
                                    <br />
                                    <span className="text-amber-300/80">Vorab</span> = ET-Jahr {programmStats.vorabYear} (potenziell zu früh produziert).
                                </>
                            ) : (
                                <>
                                    Titel mit Auflagen-Aktion im Bewertungsjahr {programmStats.periodYear} (AA = {programmStats.periodYear}).
                                    <br />
                                    <span className="text-cyan-300/80">Auflage 3+</span> = bereits 3. oder höhere Auflage im selben Jahr — bemerkenswerter Verkaufserfolg.
                                </>
                            )}
                        </p>
                    </div>
                </div>

                {/* Gesamt-Block: 3 nebeneinander, breit über die ganze Kachel */}
                <div
                    className={`grid grid-cols-1 sm:grid-cols-3 gap-0 rounded-md border divide-y sm:divide-y-0 sm:divide-x mb-5 ${theme.box}`}
                    data-testid="programm-summary-totals"
                >
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {theme.primaryCountLabel(programmStats)}
                        </div>
                        <div className={`font-heading text-3xl font-semibold tabular mt-1 ${theme.primary}`} data-testid="programm-total-count">
                            {num(programmStats.totalSel)}
                            <span className="text-base text-muted-foreground font-normal"> / {num(programmStats.total)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground tabular mt-1">
                            {programmStats.quote.toFixed(1).replace(".", ",")} % aller Bewegungen
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {theme.subLabel(programmStats)}
                        </div>
                        <div className={`font-heading text-3xl font-semibold tabular mt-1 ${theme.sub}`} data-testid="programm-sub-count">
                            {num(programmStats.totalSub)}
                            {programmStats.totalSel > 0 && (
                                <span className="text-base text-muted-foreground font-normal">
                                    {" "}({((programmStats.totalSub / programmStats.totalSel) * 100).toFixed(0)} % der {theme.subSuffix})
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground tabular mt-1">
                            Lagerwert {eur(programmStats.lager_sub)}
                            {programmStats.lager_sel > 0 && (
                                <> · {((programmStats.lager_sub / programmStats.lager_sel) * 100).toFixed(0)} % des {theme.subSuffix}-Werts</>
                            )}
                        </div>
                    </div>
                    <div className="p-4">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            {theme.lagerLabel}
                        </div>
                        <div className={`font-heading text-3xl font-semibold tabular mt-1 ${theme.primary}`} data-testid="programm-total-lagerwert">
                            {eur(programmStats.lager_sel)}
                        </div>
                        <div className="text-xs text-muted-foreground tabular mt-1">
                            {programmStats.lager_quote.toFixed(1).replace(".", ",")} % von {eur(programmStats.lager_total)}
                        </div>
                    </div>
                </div>

                {programmStats.groups.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="programm-by-erloesgruppe">
                        {programmStats.groups.map((g) => (
                            <div
                                key={g.erloesgruppe}
                                className={`rounded-md border p-4 ${theme.egBox}`}
                                data-testid={`programm-eg-${g.kuerzel}`}
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className={`shrink-0 w-10 h-10 rounded-full font-bold flex items-center justify-center text-sm border ${theme.egCircle}`}>
                                        {g.kuerzel}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-muted-foreground truncate" title={g.erloesgruppe}>
                                            {g.erloesgruppe}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/80">
                                            Quote {g.quote.toFixed(1).replace(".", ",")} %
                                        </div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-[auto_auto_1fr] gap-3 text-right">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{theme.egCountLabel}</div>
                                        <div className={`font-heading text-lg font-semibold tabular whitespace-nowrap ${theme.primary}`}>
                                            {num(g.sel)}
                                            <span className="text-[10px] text-muted-foreground font-normal"> / {num(g.total)}</span>
                                        </div>
                                    </div>
                                    <div className={`border-l pl-3 ${theme.egDivider}`}>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{theme.egSubLabel}</div>
                                        <div className={`font-heading text-lg font-semibold tabular whitespace-nowrap ${theme.sub}`}>
                                            {num(g.sub)}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground tabular whitespace-nowrap">
                                            {eur(g.lager_sub)}
                                        </div>
                                    </div>
                                    <div className={`border-l pl-3 min-w-0 ${theme.egDivider}`}>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{theme.egLagerLabel}</div>
                                        <div className={`font-heading text-lg font-semibold tabular whitespace-nowrap ${theme.primary}`}>
                                            {eur(g.lager_sel)}
                                        </div>
                                    </div>
                                </div>

                                {/* Schlanker Anteils-Balken */}
                                <div className={`mt-3 pt-3 border-t ${theme.barTopBorder}`} title={`${theme.cardLabel} binden ${eur(g.lager_sel)} von ${eur(g.lager_total)} Gesamt-Lagerwert`}>
                                    <div className="flex items-center justify-between text-[10px] text-muted-foreground tabular mb-1">
                                        <span className="uppercase tracking-widest">Anteil am Lagerwert</span>
                                        <span className={`font-medium ${theme.barPercent}`}>
                                            {g.lager_quote.toFixed(1).replace(".", ",")} %
                                        </span>
                                    </div>
                                    <div className={`h-1.5 rounded-full overflow-hidden ${theme.barBg}`}>
                                        <div
                                            className={`h-full rounded-full transition-all ${theme.barFill}`}
                                            style={{ width: `${Math.min(100, g.lager_quote)}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {programmStats.total === 0 && (
                    <div className="text-sm text-muted-foreground mt-4">
                        Keine Bewegungsdaten für {zeitraum} vorhanden.
                    </div>
                )}
            </Card>

            {/* Tag-Liste (NE jetzt, ND/R/M folgt) */}
            <Card className="p-6 bg-surface1 border-border" data-testid="tag-list-card">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Tag-Listen</div>
                        <h3 className="font-heading text-xl font-semibold mt-1">
                            Titel nach Auto-Tag
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                            {activeTagDef?.title} · {activeTagDef?.description}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {aaLueckenCount > 0 && (
                            <button
                                type="button"
                                onClick={exportAaLuecken}
                                data-testid="tag-aaluecken-export-btn"
                                className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 hover:bg-yellow-500/20 text-sm font-medium text-yellow-200 transition-colors"
                                title={`${aaLueckenCount} Bewegungen haben kein AA-Jahr, aber Auflage ≥ 2 — potenzielle versteckte Nachdrucke`}
                            >
                                <FileSpreadsheet className="h-4 w-4" />
                                Datenlücken AA ({aaLueckenCount})
                            </button>
                        )}
                        {conflictCount > 0 && (
                            <button
                                type="button"
                                onClick={exportTagConflicts}
                                data-testid="tag-conflict-export-btn"
                                className="inline-flex items-center gap-2 h-10 px-3 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-sm font-medium text-rose-200 transition-colors"
                                title={`${conflictCount} Titel haben mehrere Tags (NE + ND) — als Excel exportieren`}
                            >
                                <FileSpreadsheet className="h-4 w-4" />
                                Konflikte ({conflictCount})
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={exportTagExcel}
                            disabled={!tagCounts[activeTag]}
                            data-testid="tag-export-btn"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-border bg-surface2 hover:bg-surface1 text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={`Tag-Liste „${activeTagDef?.label}" als Excel exportieren`}
                        >
                            <FileSpreadsheet className="h-4 w-4 text-emerald-300" />
                            Excel-Export
                        </button>
                    </div>
                </div>

                {/* Tag-Tabs */}
                <div className="flex flex-wrap gap-2 mb-4" data-testid="tag-tabs">
                    {TAG_DEFS.map((t) => {
                        const active = activeTag === t.key;
                        const count = tagCounts[t.key] || 0;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setActiveTag(t.key)}
                                data-testid={`tag-tab-${t.key}`}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors flex items-center gap-2 ${
                                    active
                                        ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                                        : "border-border text-muted-foreground hover:bg-surface2 hover:text-foreground"
                                }`}
                                title={t.description}
                            >
                                <span className="font-bold tracking-wider">{t.label}</span>
                                <span className="text-xs opacity-80">{count}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Sub-KPI: Anzahl + Lagerwert für aktiven Tag */}
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm mb-3 px-1">
                    <div>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">
                            Anzahl
                        </span>
                        <span className="font-heading text-lg tabular text-amber-300">
                            {num(tagCounts[activeTag] || 0)}
                        </span>
                    </div>
                    <div>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-2">
                            Lagerwert
                        </span>
                        <span className="font-heading text-lg tabular text-amber-300">
                            {eur(tagLagerwerte[activeTag] || 0)}
                        </span>
                    </div>
                </div>

                <div className="overflow-auto border border-border rounded-md max-h-[520px]" data-testid="tag-list-scroll">
                    <Table>
                        <TableHeader className="bg-surface2 sticky top-0 z-10">
                            <TableRow>
                                <TableHead>ISBN</TableHead>
                                <TableHead>Titel</TableHead>
                                <TableHead>Erlösgruppe</TableHead>
                                <TableHead className="text-right">ET</TableHead>
                                <TableHead className="text-right">Lagerwert</TableHead>
                                <TableHead className="text-right">Bilanzwert</TableHead>
                                <TableHead className="text-center">Tag</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {tagItems.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                                        Keine Titel mit Tag „{activeTagDef?.label}&quot; im Jahr {zeitraum}.
                                    </TableCell>
                                </TableRow>
                            )}
                            {tagItems.map((m) => (
                                <TableRow key={m.id || `${m.isbn}-${m.zeitraum}`}>
                                    <TableCell className="font-mono text-xs">{m.isbn}</TableCell>
                                    <TableCell className="max-w-[320px] truncate" title={m.haupttitel}>
                                        {m.haupttitel}
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {m.erloesgruppe || "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular text-xs">
                                        {m.et_jahr || "—"}
                                    </TableCell>
                                    <TableCell className="text-right tabular">
                                        {eur(m.lagerwert)}
                                    </TableCell>
                                    <TableCell className="text-right tabular font-semibold">
                                        {eur(m.bilanzwert)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <span className={`text-[9px] py-0 px-1.5 rounded font-bold uppercase tracking-widest border ${
                                            activeTag === "ne"
                                                ? "bg-amber-500/20 text-amber-300 border-amber-500/40"
                                                : activeTag === "nd"
                                                    ? "bg-teal-500/20 text-teal-300 border-teal-500/40"
                                                    : activeTag === "r"
                                                        ? "bg-rose-500/20 text-rose-300 border-rose-500/40"
                                                        : "bg-purple-500/20 text-purple-300 border-purple-500/40"
                                        }`}>
                                            {activeTagDef?.label}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </Card>

            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(o) => setDlDialog((d) => ({ ...d, open: o }))}
                url={dlDialog.url}
                title="Tag-Liste als Excel herunterladen"
                description="Der Download wird in einem neuen Tab gestartet. Falls Ihr Browser den neuen Tab blockiert, kopieren Sie die URL und öffnen Sie sie manuell."
                filename={dlDialog.filename}
                expiresInSec={dlDialog.expiresInSec}
            />
        </div>
    );
}
